import { Block, Text } from '@snowflake/core-ui';
import * as React from 'react';
import { CheckmarkIcon, CopyIcon } from './terminalIcons';

export type TerminalCommand =
  | { label?: string; command: string; hint?: never }
  | { label?: string; command?: never; hint: string };

export interface TerminalTab {
  label: string;
  badge?: string;
  commands: TerminalCommand[];
}

export interface TerminalCodeBlockProps {
  tabs: TerminalTab[];
  defaultTab?: number;
}

const MONOSPACE_FONT = "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace";

export const TerminalCodeBlock: React.FC<TerminalCodeBlockProps> = ({ tabs, defaultTab = 0 }) => {
  const [activeTab, setActiveTab] = React.useState(defaultTab);
  const [copiedIndex, setCopiedIndex] = React.useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  const handleCopy = async (command: string, index: number) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    setCopiedIndex(null);
  };

  return (
    <Block
      borderRadius="12px"
      overflow="hidden"
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Tab Bar */}
      <Block display="flex" style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
        {tabs.map((tab, index) => (
          <Block
            key={tab.label}
            paddingTop={8}
            paddingBottom={8}
            paddingLeft={12}
            paddingRight={12}
            display="flex"
            alignItems="center"
            style={{
              cursor: 'pointer',
              gap: '6px',
              backgroundColor: activeTab === index ? '#ffffff' : 'transparent',
              borderBottom: activeTab === index ? '2px solid #ffffff' : '2px solid transparent',
              transition: 'all 0.2s ease',
            }}
            onClick={() => handleTabClick(index)}
          >
            <Text
              style={{
                fontSize: '14px',
                color: activeTab === index ? '#0B6D9F' : 'rgba(0, 0, 0, 0.6)',
                fontWeight: 400,
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </Text>
            {tab.badge && (
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 500,
                  color: '#0B6D9F',
                  backgroundColor: '#E3F2FD',
                  borderRadius: '10px',
                  padding: '1px 6px',
                  whiteSpace: 'nowrap',
                  lineHeight: '16px',
                }}
              >
                {tab.badge}
              </span>
            )}
          </Block>
        ))}
      </Block>

      {/* Command Display */}
      <div style={{ padding: '12px 24px' }}>
        {tabs.map((tab, tabIndex) => (
          <Block
            key={tab.label}
            flexDirection="column"
            style={{ gap: '10px', display: activeTab === tabIndex ? 'flex' : 'none' }}
          >
            {tab.commands.map((cmd, idx) => (
              <Block
                key={cmd.label ?? idx}
                display="flex"
                flexDirection="column"
                style={{ gap: '2px' }}
              >
                {cmd.label && (
                  <Text
                    style={{
                      color: 'rgba(0, 0, 0, 0.45)',
                      fontFamily: MONOSPACE_FONT,
                      fontSize: '11px',
                      lineHeight: '16px',
                    }}
                  >
                    {cmd.label}:
                  </Text>
                )}
                {cmd.command ? (
                  <Block display="flex" justifyContent="space-between" alignItems="center">
                    <Block
                      display="flex"
                      alignItems="flex-start"
                      style={{ flex: 1, marginRight: '8px' }}
                    >
                      <Text
                        style={{
                          color: '#085BD7',
                          marginRight: '8px',
                          fontFamily: MONOSPACE_FONT,
                          fontSize: '12px',
                          flexShrink: 0,
                          lineHeight: '22px',
                        }}
                      >
                        &gt;
                      </Text>
                      <Text
                        style={{
                          color: 'rgba(0, 0, 0, 0.9)',
                          fontFamily: MONOSPACE_FONT,
                          fontSize: '12px',
                          wordBreak: 'break-all',
                          lineHeight: '22px',
                        }}
                      >
                        {cmd.command}
                      </Text>
                    </Block>
                    <Block
                      padding="8px"
                      style={{
                        cursor: 'pointer',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s ease',
                        flexShrink: 0,
                        backgroundColor:
                          hoveredIndex === idx ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                      }}
                      onClick={() => handleCopy(cmd.command!, idx)}
                      onMouseEnter={() => setHoveredIndex(idx)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      {copiedIndex === idx ? <CheckmarkIcon /> : <CopyIcon />}
                    </Block>
                  </Block>
                ) : (
                  <Text
                    style={{
                      color: 'rgba(0, 0, 0, 0.9)',
                      fontSize: '12px',
                      lineHeight: '22px',
                    }}
                  >
                    {cmd.hint}
                  </Text>
                )}
              </Block>
            ))}
          </Block>
        ))}
      </div>
    </Block>
  );
};
